/**
 * User Entity
 * Domain model representing a user in the system
 */
export class UserEntity {
    constructor({
        id,
        name,
        firstName,
        lastName,
        phone,
        email,
        avatar,
        memberStatus,
        creditBalance,
        points,
        title,
    }) {
        this.id = id;
        this.name = name;
        this.firstName = firstName;
        this.lastName = lastName;
        this.phone = phone;
        this.email = email;
        this.avatar = avatar;
        this.memberStatus = memberStatus || 'Member';
        this.creditBalance = creditBalance || 0;
        this.points = points || 0;
        this.title = title || '';
    }

    /**
     * Get full name
     */
    getFullName() {
        if (this.firstName && this.lastName) {
            return `${this.firstName} ${this.lastName}`;
        }
        return this.name || '';
    }

    /**
     * Check if user has sufficient credit balance
     */
    hasSufficientCredit(amount) {
        return this.creditBalance >= amount;
    }

    /**
     * Check if user has sufficient points
     */
    hasSufficientPoints(amount) {
        return this.points >= amount;
    }
}

/**
 * Transaction Entity
 * Domain model for credit/points transactions
 */
export class TransactionEntity {
    constructor({ id, type, amount, description, date, status }) {
        this.id = id;
        this.type = type; // 'credit' or 'points'
        this.amount = amount;
        this.description = description;
        this.date = date;
        this.status = status || 'completed';
    }
}

/**
 * Voucher Entity
 * Domain model for user vouchers
 */
export class VoucherEntity {
    constructor({ id, code, name, description, discount, expiryDate, status, imageUrl }) {
        this.id = id;
        this.code = code;
        this.name = name;
        this.description = description;
        this.discount = discount;
        this.expiryDate = expiryDate;
        this.status = status || 'active';
        this.imageUrl = imageUrl;
    }

    isExpired() {
        return new Date(this.expiryDate) < new Date();
    }

    isActive() {
        return this.status === 'active' && !this.isExpired();
    }
}

/**
 * Coupon Entity
 * Domain model for user coupons
 */
export class CouponEntity {
    constructor({ id, code, name, description, discount, expiryDate, status, imageUrl }) {
        this.id = id;
        this.code = code;
        this.name = name;
        this.description = description;
        this.discount = discount;
        this.expiryDate = expiryDate;
        this.status = status || 'active';
        this.imageUrl = imageUrl;
    }

    isExpired() {
        return new Date(this.expiryDate) < new Date();
    }

    isActive() {
        return this.status === 'active' && !this.isExpired();
    }
}

/**
 * Order Entity
 * Domain model for user orders
 */
export class OrderEntity {
    constructor({ id, orderNumber, date, items, total, status, paymentMethod }) {
        this.id = id;
        this.orderNumber = orderNumber;
        this.date = date;
        this.items = items || [];
        this.total = total;
        this.status = status || 'pending';
        this.paymentMethod = paymentMethod;
    }

    isPending() {
        return this.status === 'pending';
    }

    isCompleted() {
        return this.status === 'completed';
    }

    isCancelled() {
        return this.status === 'cancelled';
    }
}
